import { request } from './api'
import {
  acceptVillageRealtimeRevision,
  reconcileActiveVillages,
} from './liveSync'

interface RealtimeAuthorization {
  hub: string
  topic: string
}

interface VillageRealtimeEvent {
  eventId: string
  eventName: string
  worldId: string
  villageId: string
  villageRevision: number
  occurredAt: string
}

const AUTH_REFRESH_MS = 45 * 60 * 1_000
const VISIBILITY_REFRESH_AFTER_MS = 30 * 60 * 1_000
const AUTH_RETRY_MS = [1_000, 2_000, 5_000, 15_000, 30_000] as const
const MAX_SEEN_EVENTS = 512

function validEvent(value: unknown): value is VillageRealtimeEvent {
  if (value === null || typeof value !== 'object') return false
  const row = value as Record<string, unknown>
  return (
    typeof row.eventId === 'string' &&
    row.eventId !== '' &&
    typeof row.eventName === 'string' &&
    typeof row.worldId === 'string' &&
    row.worldId !== '' &&
    typeof row.villageId === 'string' &&
    row.villageId !== '' &&
    Number.isSafeInteger(row.villageRevision) &&
    Number(row.villageRevision) >= 0 &&
    typeof row.occurredAt === 'string'
  )
}

export async function clearRealtimeAuthorization(): Promise<void> {
  if (typeof window === 'undefined') return
  try {
    await fetch('/api/v2/realtime/authorization', {
      method: 'DELETE',
      credentials: 'same-origin',
      cache: 'no-store',
      keepalive: true,
      headers: { Accept: 'application/json' },
    })
  } catch {
    // The cookie is short-lived. Failure to clear it during a network outage
    // cannot authorize API access and is retried on the next explicit logout.
  }
}

export function startRealtime(): () => void {
  if (typeof window === 'undefined' || typeof EventSource === 'undefined')
    return () => {
      // Realtime is unavailable during SSR or non-browser tests.
    }

  let stopped = false
  let source: EventSource | null = null
  let refreshTimer: number | null = null
  let retryTimer: number | null = null
  let retryAttempt = 0
  let lastAuthorizationAt = 0
  let authorizationInFlight: Promise<void> | null = null
  const isStopped = (): boolean => stopped
  const seen = new Set<string>()
  const seenOrder: string[] = []

  const rememberEvent = (eventId: string): boolean => {
    if (seen.has(eventId)) return false
    seen.add(eventId)
    seenOrder.push(eventId)
    while (seenOrder.length > MAX_SEEN_EVENTS) {
      const oldest = seenOrder.shift()
      if (oldest !== undefined) seen.delete(oldest)
    }
    return true
  }

  const clearRetry = (): void => {
    if (retryTimer !== null) window.clearTimeout(retryTimer)
    retryTimer = null
    retryAttempt = 0
  }

  const scheduleRefresh = (): void => {
    if (refreshTimer !== null) window.clearTimeout(refreshTimer)
    refreshTimer = window.setTimeout(() => {
      refreshTimer = null
      void authorize(true)
    }, AUTH_REFRESH_MS)
  }

  const scheduleAuthorizationRetry = (): void => {
    if (stopped || retryTimer !== null || !navigator.onLine) return
    const delay =
      AUTH_RETRY_MS[Math.min(retryAttempt, AUTH_RETRY_MS.length - 1)]
    retryAttempt += 1
    retryTimer = window.setTimeout(() => {
      retryTimer = null
      void authorize(true)
    }, delay)
  }

  const openSource = (authorization: RealtimeAuthorization): void => {
    if (stopped) return
    const hub = new URL(authorization.hub, window.location.origin)
    hub.searchParams.append('topic', authorization.topic)
    const next = new EventSource(hub.toString(), { withCredentials: true })
    source?.close()
    source = next
    next.addEventListener('open', () => {
      if (source !== next || stopped) return
      clearRetry()
      window.dispatchEvent(new Event('ci:realtime-open'))
    })
    next.addEventListener('village', (message) => {
      if (source !== next || stopped || !(message instanceof MessageEvent))
        return
      let parsed: unknown
      try {
        parsed = JSON.parse(String(message.data)) as unknown
      } catch {
        return
      }
      if (!validEvent(parsed) || !rememberEvent(parsed.eventId)) return
      acceptVillageRealtimeRevision(
        parsed.worldId,
        parsed.villageId,
        parsed.villageRevision,
      )
    })
    next.addEventListener('error', () => {
      if (source !== next || stopped) return
      if (next.readyState === EventSource.CLOSED) scheduleAuthorizationRetry()
    })
  }

  const authorize = async (reopen: boolean): Promise<void> => {
    if (stopped) return
    if (authorizationInFlight !== null) return authorizationInFlight
    authorizationInFlight = (async () => {
      try {
        const authorization = await request<RealtimeAuthorization>(
          '/api/v2/realtime/authorization',
          { method: 'POST', readOnly: true, cache: 'no-store' },
        )
        if (isStopped()) return
        lastAuthorizationAt = Date.now()
        if (reopen || source === null) openSource(authorization)
        scheduleRefresh()
      } catch {
        // Central API auth handling owns real 401 logout. Only connection or
        // transport failures are retried here; this never polls queue state.
        scheduleAuthorizationRetry()
      }
    })()
    try {
      await authorizationInFlight
    } finally {
      authorizationInFlight = null
    }
  }

  const onVisibility = (): void => {
    if (document.hidden || stopped) return
    if (Date.now() - lastAuthorizationAt >= VISIBILITY_REFRESH_AFTER_MS)
      void authorize(true)
    reconcileActiveVillages()
  }
  const onOnline = (): void => {
    if (stopped) return
    clearRetry()
    void authorize(true)
    reconcileActiveVillages()
  }

  document.addEventListener('visibilitychange', onVisibility)
  window.addEventListener('online', onOnline)
  void authorize(true)

  return () => {
    if (stopped) return
    stopped = true
    source?.close()
    source = null
    if (refreshTimer !== null) window.clearTimeout(refreshTimer)
    refreshTimer = null
    clearRetry()
    document.removeEventListener('visibilitychange', onVisibility)
    window.removeEventListener('online', onOnline)
  }
}
